import { morphism } from 'morphism'
import { omit, path, pathOr } from 'ramda'
import { Beneficiary } from 'types/finance'
import { currencyUtils } from '../../../utils'
import { getInvoiceTypeDisplayName } from './utils'
import {
  TBeneficiaryRequest,
  TBulkInvoicesResponse,
  TCreateInvoiceRequest,
  TInvoice,
  TInvoiceTypeSummary,
  TNewInvoiceValues,
  TStagedInvoicesResponse,
  TUpdateInvoiceBody,
  TUpdateInvoiceRequestBody,
} from './types'

const { toCents, fromCents } = currencyUtils

/**
 * Every amount on the wire is in rands; every amount in the store is in cents. These
 * schemas are the only place the two representations meet, so the `T*` types on either
 * side of a transformer describe the same field set in different units.
 *
 * `morphism`'s return type never overlaps its target, hence the `as unknown as T` on
 * each exported transformer rather than a plain `as T`.
 */

const toCentsAmount = (amount?: number) => toCents(amount).getAmount()

/**
 * A "Not set" draft has no amount yet, and the API rejects an amount of `0` — which is what an
 * unset amount becomes once it has been through `toCents` on the way in. Send `null` instead, so
 * the amount stays unset and the rest of the draft (payment rules, due date, description) saves.
 */
const unsetAmountToNull = (amount?: number): number | null => (amount ? fromCents(amount) : null)

/**
 * A beneficiary's `amount` is the only monetary field on it, and it sits on the inner
 * `value` regardless of which variant the row is — so each of these maps the amount and
 * leaves the discriminant and the rest of the value untouched.
 */
const beneficiaryAmountTo =
  (convert: (amount?: number) => number | null) =>
  ({ beneficiaries = [] }: { beneficiaries?: Beneficiary[] }) =>
    beneficiaries.map(b => ({
      ...b,
      beneficiary: {
        ...b.beneficiary,
        value: { ...b.beneficiary.value, amount: convert(b.beneficiary.value.amount) },
      },
    }))

/** The request encoding drops the rule wrapper and posts `{ type, value }` directly. */
const beneficiaryRulesToRequest =
  (convert: (amount?: number) => number | null) =>
  ({ beneficiaries = [] }: { beneficiaries?: Beneficiary[] }): TBeneficiaryRequest[] =>
    beneficiaries.map(({ beneficiary }) => ({
      type: beneficiary.type,
      value: { ...beneficiary.value, amount: convert(beneficiary.value.amount) },
    }))

const invoiceResponseSchema = {
  id: 'id',
  tags: 'tags',
  number: 'number',
  customer: 'customer',
  portfolioId: 'portfolioId',
  property: 'property',
  reoccurring: 'reoccurring',
  invoiceType: ({ invoiceType }: TInvoice) => getInvoiceTypeDisplayName(invoiceType),
  invoiceTypeName: ({ invoiceTypeName }: TInvoice) => getInvoiceTypeDisplayName(invoiceTypeName),
  dueDate: 'dueDate',
  vat: 'vat',
  amount: ({ amount }: TInvoice) => toCentsAmount(amount),
  balance: ({ balance }: TInvoice) => toCentsAmount(balance),
  beneficiaries: beneficiaryAmountTo(toCentsAmount),
  description: 'description',
  mainText: 'mainText',
  secondaryText: 'secondaryText',
  metaData: 'metaData',
}

/**
 * `morphism` maps an array source element-wise, so this is overloaded to mirror what the
 * caller passed in: the entity and update endpoints hand it an object, the list endpoint
 * an array.
 */
export function transformInvoiceResponse(source: TInvoice): TInvoice
export function transformInvoiceResponse(source: TInvoice[]): TInvoice[]
export function transformInvoiceResponse(source: TInvoice | TInvoice[]): TInvoice | TInvoice[]
export function transformInvoiceResponse(source: TInvoice | TInvoice[]): TInvoice | TInvoice[] {
  return morphism(invoiceResponseSchema, source as never) as unknown as TInvoice | TInvoice[]
}

const invoiceTypesResponseSchema = {
  name: ({ name }: TInvoiceTypeSummary) => (name === 'Application-Fee' ? 'Application Fee' : name),
  value: 'value',
  incomeType: 'incomeType',
  vat: 'vat',
}

export const transformInvoiceTypesResponse = (source: TInvoiceTypeSummary[]): TInvoiceTypeSummary[] =>
  morphism(invoiceTypesResponseSchema, source as never) as unknown as TInvoiceTypeSummary[]

const updateInvoiceSchema = {
  number: 'number',
  customer: 'customer',
  portfolioId: 'portfolioId',
  customerTag: 'customerTag',
  invoiceType: 'invoiceType',
  dueDate: 'dueDate',
  vat: 'vat',
  amount: ({ amount }: TUpdateInvoiceBody) => unsetAmountToNull(amount),
  beneficiaries: beneficiaryRulesToRequest(unsetAmountToNull),
  description: 'description',
}

/**
 * The source here is the *flattened* update body the epic builds — `customer` is already
 * a party id and `customerTag` has been lifted out of it — not the form's own values.
 *
 * The schema used to map `id`, `tags`, `property`, `reoccurring` and `invoiceTypeName`
 * too. The epic omits all five from the source before calling this, so every one of them
 * always resolved to `undefined` and `JSON.stringify` dropped it: the request on the wire
 * is unchanged.
 */
export const transformUpdateInvoiceRequest = (source: TUpdateInvoiceBody): TUpdateInvoiceRequestBody =>
  morphism(updateInvoiceSchema, source as never) as unknown as TUpdateInvoiceRequestBody

const newInvoiceRequestSchema = {
  id: 'id',
  customer: 'customer',
  customerName: 'customerName',
  invoiceType: 'invoiceType',
  dueDate: 'dueDate',
  propertyAddress: 'propertyAddress',
  vat: 'vat',
  customerTag: 'customerTag',
  portfolioId: (r: TNewInvoiceValues) => pathOr(false, ['portfolioId'], r),
  amount: ({ amount }: TNewInvoiceValues) => fromCents(amount),
  beneficiaries: beneficiaryRulesToRequest(fromCents),
  description: 'description',
}

/**
 * A consolidated-balance invoice has no portfolio, and the API rejects an explicit
 * `false`, so the key is dropped rather than sent — which is what the schema's `false`
 * default is for.
 */
export const transformNewInvoiceRequest = (source: TNewInvoiceValues): TCreateInvoiceRequest => {
  const request = morphism(newInvoiceRequestSchema, source as never)
  return (path(['portfolioId'], request) ? request : omit(['portfolioId'], request)) as unknown as TCreateInvoiceRequest
}

const newInvoiceResponseSchema = {
  id: 'id',
  tags: 'tags',
  customer: 'customer',
  reoccurring: 'reoccurring',
  invoiceType: 'invoiceType',
  invoiceTypeName: 'invoiceTypeName',
  dueDate: 'dueDate',
  vat: 'vat',
  amount: ({ amount }: TInvoice) => toCentsAmount(amount),
  balance: ({ balance }: TInvoice) => toCentsAmount(balance),
  beneficiaries: beneficiaryAmountTo(toCentsAmount),
}

/**
 * Narrower than {@link transformInvoiceResponse} — the create response carries no
 * `portfolioId`, `property`, `number` or text fields — and it deliberately does *not*
 * run `invoiceType` through `getInvoiceTypeDisplayName`, so a legacy 'Application-Fee'
 * survives here where the other transformer would rewrite it.
 */
export const transformNewInvoiceResponse = (source: TInvoice): TInvoice =>
  morphism(newInvoiceResponseSchema, source) as unknown as TInvoice

const getBulkInvoicesResponseSchema = {
  id: 'id',
  customer: 'customer',
  customerName: 'customerName',
  propertyAddress: 'propertyAddress',
  invoiceType: 'invoiceType',
  dueDate: 'dueDate',
  vat: 'vat',
  customerTag: 'customerTag',
  portfolioId: (r: TCreateInvoiceRequest) => pathOr(false, ['portfolioId'], r),
  amount: ({ amount }: TCreateInvoiceRequest) => toCentsAmount(amount ?? undefined),
  /**
   * A staged row holds its beneficiaries in the bare request encoding, so this re-wraps
   * each one into the rule shape the review table renders as well as converting the
   * amount — leaving an amountless beneficiary as `''` rather than 0, so the table's
   * currency input renders empty.
   */
  beneficiaries: ({ beneficiaries = [] }: TCreateInvoiceRequest) =>
    beneficiaries.map(b => ({
      beneficiary: {
        ...b,
        value: { ...b.value, amount: b.value?.amount ? toCentsAmount(b.value.amount) : '' },
      },
    })),
  description: 'description',
}

export const transformGetBulkInvoicesResponse = (source: TBulkInvoicesResponse | null): TStagedInvoicesResponse =>
  morphism(
    {
      invoices: ({ invoices = [] }: TBulkInvoicesResponse) =>
        invoices.map(invoice => morphism(getBulkInvoicesResponseSchema, invoice)),
    },
    source,
  ) as unknown as TStagedInvoicesResponse
